import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

// Core Components
import MDCModal from "../../../core/MDCModal";
import MDCButton from "../../../core/MDCButton";

// Assets
import carLogo from "../../../../assets/images/my_digital_car.svg";

// Actions
import { userActions } from "../../../../store/actions/userActions";

// Constants
import {
  TIME_LIMIT,
  FULL_DASH_ARRAY,
  LOGOUT_WARNING_MESSAGE,
  LOGOUT_WARNING_HEADER_LABEL,
  EXTEND_SESSION_BUTTON_LABEL,
} from "../../../../constants/userConstants";

// Material UI
import { makeStyles } from "@mui/styles";
import { Grid } from "@mui/material";

// Styles
const useStyles = makeStyles((theme) => ({
  headerCss: {
    padding: "2rem",
    alignItems: "center",
  },

  headerColor: { color: theme.palette.MDCColors.black },
  labelColor: { color: theme.palette.MDCColors.color18 },

  baseTimer: {
    position: "relative",
    width: "8rem",
    height: "8rem",
    margin: "auto",
  },
  baseTimerSVG: {
    transform: "scaleX(1)",
  },
  baseTimerCircle: {
    fill: "none",
    stroke: "none",
  },
  baseTimerPathElapsed: {
    strokeWidth: "6px",
    stroke: "#efefef",
  },

  baseTimerPathRemaining: {
    strokeWidth: "4px",
    strokeLinecap: "round",
    transform: "rotate(90deg)",
    transformOrigin: "center",
    transition: "1s linear all",
    fillRule: "nonzero",
    stroke: "#00C4DE",
  },

  baseTimerLabel: {
    position: "absolute",
    width: "8rem",
    height: "8rem",
    top: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
    fontWeight: "600",
    letterSpacing: "0.3px",
    color: "#5C5B5B",
  },
}));

const SessionTimeoutWarning = (props) => {
  const [isSessionExtended, setIsSessionExtended] = useState(false); //local state to check if session is extended or not
  const styles = useStyles(); //CSS classes object
  const [reverseTimer, setReverseTimer] = useState(TIME_LIMIT); //local component state
  const timerIntervalRef = useRef(null);
  const deadlineRef = useRef(null);
  const hasLoggedOutRef = useRef(false);

  const dispatch = useDispatch();

  function calculateTimeFraction(currentTimeLeft) {
    // calculate fraction to set stroke-dasharray(i.e circle length) property in rotating svg circle
    const rawTimeFraction = currentTimeLeft / TIME_LIMIT;
    return rawTimeFraction - (1 / TIME_LIMIT) * (1 - rawTimeFraction);
  }

  function setCircleDasharray(currentTimeLeft) {
    // will set moving circle length according to time fraction
    const circleDasharray = `${(
      calculateTimeFraction(currentTimeLeft) * FULL_DASH_ARRAY
    ).toFixed(0)} 283`;
    const timerPathElement = document.getElementById(
      "base-timer-path-remaining"
    );
    if (timerPathElement) {
      timerPathElement.setAttribute("stroke-dasharray", circleDasharray);
    }
  }

  function clearTimer() {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }

  function updateTimerDisplay() {
    if (!deadlineRef.current || hasLoggedOutRef.current) {
      return;
    }

    const currentTimeLeft = Math.max(
      0,
      Math.ceil((deadlineRef.current - Date.now()) / 1000)
    );

    setCircleDasharray(currentTimeLeft);
    setReverseTimer(currentTimeLeft);

    if (currentTimeLeft === 0) {
      hasLoggedOutRef.current = true;
      clearTimer();
      props.handleLogout();
    }
  }

  function startTimer() {
    clearTimer();
    hasLoggedOutRef.current = false;
    deadlineRef.current = Date.now() + TIME_LIMIT * 1000;

    setReverseTimer(TIME_LIMIT);
    setCircleDasharray(TIME_LIMIT);
    updateTimerDisplay();

    timerIntervalRef.current = setInterval(() => {
      updateTimerDisplay();
    }, 1000);
  }

  useEffect(() => {
    if (props.show) {
      startTimer(); //will start the timer as soon as warning modal is shown
    } else {
      clearTimer();
    }

    return () => {
      clearTimer();
    };
  }, [props.show]);

  useEffect(() => {
    if (!props.show) {
      return undefined;
    }

    const syncTimerOnVisibilityChange = () => {
      updateTimerDisplay();
    };

    document.addEventListener("visibilitychange", syncTimerOnVisibilityChange);
    window.addEventListener("focus", syncTimerOnVisibilityChange);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        syncTimerOnVisibilityChange
      );
      window.removeEventListener("focus", syncTimerOnVisibilityChange);
    };
  }, [props.show]);

  const getTimerView = () => {
    return (
      <div className={styles.baseTimer}>
        <svg
          className={styles.baseTimerSVG}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g className={styles.baseTimerCircle}>
            <circle
              className={styles.baseTimerPathElapsed}
              cx="50"
              cy="50"
              r="45"
              fill="#E8FCFF"
            />
            <path
              id="base-timer-path-remaining"
              stroke-dasharray="283"
              className={styles.baseTimerPathRemaining}
              d="
                M 50, 50
                m -45, 0
                a 45,45 0 1,0 90,0
                a 45,45 0 1,0 -90,0
              "
            />
          </g>
        </svg>
        <span id="base-timer-label" className={styles.baseTimerLabel}>
          {reverseTimer}
        </span>
      </div>
    );
  };

  function extendSession() {
    setIsSessionExtended(true);
    dispatch(userActions.showLogoutWarningTimer(false)); // will reset the timer in interceptor.js
    //This API call will automatically extends the session and also the timer warning in interceptor.js
    dispatch(userActions.getSessionTimeExtended()).then((resp) => {
      clearTimer();
      setIsSessionExtended(false);
      if (resp?.status !== 200) {
        props.handleLogout();
      }
    });
  }

  const getModalHeader = () => {
    return (
      <>
        <Grid container className={styles.headerCss}>
          <Grid item xs={8} className={styles.headerColor}>
            <h4> {LOGOUT_WARNING_HEADER_LABEL}</h4>
          </Grid>
          <Grid item xs={4}>
            <img alt="My Digital Car" src={carLogo} width="95%" />
          </Grid>
        </Grid>
      </>
    );
  };

  const getModalBody = () => {
    return (
      <Grid className={styles.headerCss}>
        <Grid container flexDirection="column">
          <Grid>{getTimerView()}</Grid>
          <Grid marginTop={"3rem"} className={styles.labelColor}>
            {LOGOUT_WARNING_MESSAGE}
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end" marginTop={"1rem"}>
          <MDCButton
            onClick={extendSession}
            disabled={isSessionExtended}
            variant="contained"
            label={EXTEND_SESSION_BUTTON_LABEL}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <MDCModal
        open={props.show}
        title={getModalHeader()}
        body={getModalBody()}
        size="sm"
      />
    </div>
  );
};

export default SessionTimeoutWarning;
